/* oxlint-disable typescript/no-explicit-any -- uses type a = any pattern for generic typing */
import MarkdownExit, { dumpFlatYaml, Token } from '@w/md'
import { INLINE_BLOCK_TYPES, tableHasSpans, tableToHtml, blocksToTableHtml } from './table-html'
import { cellInlineTable } from './cell-inline'
import { stripRuntimeState } from './runtime-state'
import {
  type Block,
  type BookBlock,
  type BookBlocks,
  type Blocks,
  type ExtractBlock,
  FormatFlag,
  diffMarker,
  isDiffCodeBlock,
  getTextFromChars,
  unwrapReviewTableCells,
  unwrapReviewTableRows,
} from './block'

// Private renderer configurations; render state lives in tokens and per-call env.
const defaultMarkdown = MarkdownExit()
const listMarkdown = createListMarkdownRenderer()

// INV-1: chars text and formats round-trip byte-exact through serialize∘parse
// in EVERY inline context (paragraph, heading, list item, table cell, caption,
// quote author). Escape spaces only where visible markdown syntax destroys them:
// full inline-run edges and immediately inside emitted format delimiters. Never
// key escaping on invisible chars segmentation.
// INV-2: pre.children plain text never encodes the fence-closing newline; the
// serializer appends it, the parser strips it.

// A directive block must use a fence strictly longer than any nested
// same-kind fence (CommonMark rule). We compute the max directive depth
// in the subtree and pick fence length = 3 + maxNestedDepth.
/** Canonical `::mention[...]` content per subtype; the parser reads the same prefixes. */
function mentionContent(block: ExtractBlock<'mention'>): string {
  if (block.subtype === 'date') return `d-${block.date}`
  const prefix: Record<typeof block.subtype, string> = { user: 'u', page: 'p' }
  return `${prefix[block.subtype]}-${block.uid}`
}

function isDirectiveBlock(block: Block): boolean {
  if (
    block.type === 'alert' ||
    block.type === 'questionGroup' ||
    block.type === 'cols' ||
    block.type === 'col' ||
    (block.type === 'p' && block.align !== undefined) ||
    (block.type === 'review' && block.display === 'block')
  ) {
    return true
  }
  if (block.type === 'quote' && (block as any).author) return true
  if (block.type === 'list' && (block.subtype === 'toggle' || (block.spread === true && block.children.length < 2))) return true
  if (block.type === 'question') {
    const inline = (block as any).inline
    return !inline
  }
  return false
}

function maxDirectiveDepth(block: Block): number {
  // A block prompt or explanation is a `:::question`/`:::explanation` container inside the question's own fence.
  const slots = questionContainerSlots(block)
  if (slots.length > 0) return 2 + Math.max(0, ...slots.map(slot => slotDirectiveDepth(slot)))
  const self = isDirectiveBlock(block) ? 1 : 0
  const children: Blocks = (block as any).children ?? []
  let maxChild = 0
  for (const child of children) {
    const d = maxDirectiveDepth(child)
    if (d > maxChild) maxChild = d
  }
  return self + maxChild
}

/** A subtyped block question whose prompt is written as a container: empty (a label-less `:::question` line would open
 * one around the slots after it) or holding blocks. */
function hasBlockPrompt(block: Block): boolean {
  const prompt = (block as any).question as Blocks | undefined
  return block.type === 'question' && !(block as any).inline && !!(block as any).subtype && !!prompt &&
    (prompt.length === 0 || !isInlineQuestionPrompt(prompt))
}

/** A subtyped block question whose explanation holds blocks (paragraphs, media), written as a `:::explanation`
 * container: the one-line `::explanation[…]` keeps inline content only. */
function hasBlockExplanation(block: Block): boolean {
  const explanation = (block as any).explanation as Blocks | undefined
  return block.type === 'question' && !(block as any).inline && !!(block as any).subtype && !!explanation &&
    explanation.length > 0 && !isInlineQuestionPrompt(explanation)
}

function questionContainerSlots(block: Block): Blocks[] {
  const slots: Blocks[] = []
  if (hasBlockPrompt(block)) slots.push((block as any).question)
  if (hasBlockExplanation(block)) slots.push((block as any).explanation)
  return slots
}

/** Directive depth inside a `:::question`/`:::explanation` slot: mixedSlotToTokens wraps a run of inline blocks (audio,
 * image) in a `:::inline` directive, which the slot's own fence must enclose. */
function slotDirectiveDepth(slot: Blocks): number {
  const inlineRun = slot.some(block => INLINE_BLOCK_TYPES.has(block.type)) ? 1 : 0
  return Math.max(inlineRun, ...slot.map(child => maxDirectiveDepth(child)))
}

function slotFence(slot: Blocks): string {
  return ':'.repeat(3 + slotDirectiveDepth(slot))
}

function fenceForChildren(children: Blocks): string {
  let max = 0
  for (const child of children) {
    const d = maxDirectiveDepth(child)
    if (d > max) max = d
  }
  // 3 colons + 1 per nested directive inside
  return ':'.repeat(3 + max)
}

export interface BlocksToMdOptions {
  trim?: boolean
  plainText?: boolean
  transform?: BlocksSerializeTransform
  pageLinkTarget?: (pageId: string, spaceId?: string) => string
  /**
   * Reader use only: literal text escaped as the writer did before context-aware escapes, for the parser's text
   * comparisons (sa answers) that stored documents must keep reading the same.
   */
  legacyTextEscape?: boolean
}

export type BlocksSerializeTransformResult =
  | string
  | Block
  | Blocks
  | null
  | undefined

export type BlocksSerializeTransform = (
  block: Block,
) => BlocksSerializeTransformResult

type AppliedTransform =
  | { type: 'default' }
  | { type: 'omit' }
  | { type: 'string'; value: string }
  | { type: 'block'; value: Block }
  | { type: 'blocks'; value: Blocks }

export function blocksToMd(
  blocks: Blocks,
  options: BlocksToMdOptions = {},
): string {
  const { trim = true, plainText = false } = options
  if (plainText) {
    const text = options.transform
      ? normalizeIndexText(blocksToPlainText(blocks, options))
      : blocksToPlainText(blocks, options)
    return trim ? text.trim() : text
  }

  const commentsBlocks = blocks.filter((block) => block.type === 'comments')
  if (commentsBlocks.length > 1) {
    throw new Error('document must contain at most one comments registry')
  }
  const documentBlocks = [
    ...blocks.filter((block) => block.type !== 'comments'),
    ...commentsBlocks,
  ]

  const mdParser = listMarkdown

  // Check if all blocks are inline blocks (text, b, i, etc.)
  // Note: img/video/audio/file/preview can be either inline or block-level
  // If they appear in blocks array, treat as block-level; so are page cards (page, pageLink, pages) standing alone, each
  // its own paragraph, or consecutive cards would join into one. Beside text they are part of the inline run.
  const pageCard = (b: Block) => ['page', 'pageLink', 'pages'].includes(b.type)
  const isAllInline = !documentBlocks.every((b) => pageCard(b)) && documentBlocks.every(
    (b) =>
      (b.type === 'review' && b.display === 'inline') ||
      pageCard(b) ||
      [
        'text',
        'chars',
        'code', // latex wrapper
        'a',
        'comment',
        'criticComment',
        'diff',
        'mention',
        'cloze',
        'em',
        'l',
        'explanation',
      ].includes(b.type),
  )

  // If all inline, serialize as inline content
  if (isAllInline && documentBlocks.length > 0) {
    const inlineTokens = escapeInlineEdgeSpaces(
      inlineBlocksToTokens(documentBlocks, true, options),
    )
    const result = mdParser.renderer.renderInline(inlineTokens, {}, {})
    return trim ? result.trim() : result
  }

  // Convert all blocks to tokens and render together
  const allTokens = blocksToTokens(documentBlocks, 0, false, options)
  // Front matter owns `---` at the start of a document; thematic breaks use the other CommonMark marker.
  if (documentBlocks.length > 1 && allTokens[0]?.type === 'hr') allTokens[0].markup = '***'

  const result = mdParser.renderer.render(allTokens, mdParser.options, {})
  return trim ? result.trim() : result
}

function createListMarkdownRenderer() {
  const mdParser = MarkdownExit()
  const closeDirective = mdParser.renderer.rules.directive_block_close
  if (closeDirective) {
    mdParser.renderer.rules.directive_block_close = (tokens, index, options, env, renderer) => {
      const result = closeDirective(tokens, index, options, env, renderer)
      const name = tokens[index - 1]?.meta?.name
      const next = tokens[index + 1]
      return (name === 'pageLink' || name === 'pages') && next?.block && !next.type.endsWith('_close')
        ? result + '\n' : result
    }
  }
  mdParser.renderer.rules.list_item_separator = () => '\n'
  mdParser.renderer.rules.list_item_content = (tokens, index) => {
    const item = tokens[index - 1]
    const indent = '  '.repeat(Math.max(0, Math.floor((item.level - 1) / 2)))
      + ' '.repeat(item.info ? item.info.length + 2 : 2)
    return tokens[index].content.split('\n').map((line, lineIndex) =>
      lineIndex === 0 || !line ? line : indent + line).join('\n')
  }
  return mdParser
}

function listItemContent(children: Blocks, options: BlocksToMdOptions): string {
  if (children.every(child => INLINE_BLOCK_TYPES.has(child.type))) return blocksToMd(children, options)
  const mdParser = listMarkdown
  const previousTokens: Token[] = []
  let content = ''
  for (const child of children) {
    const tokens = blockToTokens(child, 0, false, options, previousTokens)
    if (tokens.length === 0) continue
    const nestedList = tokens[0]?.type === 'bullet_list_open' ||
      tokens[0]?.type === 'ordered_list_open'
    const previousList = previousTokens.at(-1)?.type === 'bullet_list_close' ||
      previousTokens.at(-1)?.type === 'ordered_list_close'
    if (previousTokens.length > 0) content += nestedList && !previousList ? '\n' : '\n\n'
    content += mdParser.renderer.render(tokens, mdParser.options, {}).trim()
    previousTokens.push(...tokens)
  }
  return content
}

export function bookBlocksToMd(
  blocks: BookBlocks,
  options: BlocksToMdOptions = {},
): string {
  const trim = options.trim ?? true
  const md = blocks
    .map((block) => {
      if (block.type === 'sceneBreak' || block.type === 'hr') return '---'
      const editorBlock = bookBlockToEditorBlock(block)
      if (!editorBlock) return ''
      return blocksToMd([editorBlock], options)
    })
    .filter((part) => part.length > 0)
    .join('\n\n')
  return trim ? md.trim() : md
}

function bookBlockToEditorBlock(block: BookBlock): Block | undefined {
  switch (block.type) {
    case 'text':
      return {
        id: block.id,
        type: 'text',
        text: block.text,
        ...(block.escaped ? { escaped: block.escaped } : {}),
      }
    case 'br':
      return { id: block.id, type: 'br' }
    case 'code':
      return {
        id: block.id,
        type: 'code',
        latex: true,
        children: bookInlineBlocksToEditorBlocks(block.children),
      }
    case 'em':
      return {
        id: block.id,
        type: 'em',
        color: block.color,
        children: bookInlineBlocksToEditorBlocks(block.children),
      }
    case 'a':
      return {
        id: block.id,
        type: 'a',
        href: block.href,
        children: bookInlineBlocksToEditorBlocks(block.children),
      }
    case 'h':
      return {
        id: block.id,
        type: 'h',
        level: block.level,
        children: bookInlineBlocksToEditorBlocks(block.children),
      }
    case 'quote':
      return {
        id: block.id,
        type: 'quote',
        children: bookInlineBlocksToEditorBlocks(block.children),
      }
    case 'p':
      return {
        id: block.id,
        type: 'p',
        children: bookInlineBlocksToEditorBlocks(block.children),
      }
    case 'list':
      return {
        id: block.id,
        type: 'list',
        subtype: block.subtype,
        spread: block.spread,
        start: block.start,
        children: block.children.map((child) => ({
          id: child.id,
          type: 'li',
          done: child.done,
          spread: child.spread,
          children: bookInlineBlocksToEditorBlocks(child.children),
        })),
      }
    case 'li':
      return {
        id: block.id,
        type: 'li',
        done: block.done,
        spread: block.spread,
        children: bookInlineBlocksToEditorBlocks(block.children),
      }
    case 'table':
    case 'bookTable':
      return {
        id: block.id,
        type: 'table',
        children: block.children.map((row) => ({
          id: row.id,
          type: 'tr',
          children: row.children.map((cell) => ({
            id: cell.id,
            type: 'td',
            children: bookInlineBlocksToEditorBlocks(cell.children),
          })),
        })),
      }
    case 'pre':
      return {
        id: block.id,
        type: 'pre',
        lang: block.lang,
        ...(block.caption && {
          caption: bookInlineBlocksToEditorBlocks(block.caption),
        }),
        children: bookInlineBlocksToEditorBlocks(block.children),
      }
    case 'hr':
    case 'sceneBreak':
      return { id: block.id, type: 'hr' }
    case 'img':
      return {
        id: block.id,
        type: 'img',
        src: block.src,
        width: block.width,
        caption: block.caption
          ? bookInlineBlocksToEditorBlocks(block.caption)
          : undefined,
        children: block.children
          ? bookInlineBlocksToEditorBlocks(block.children)
          : undefined,
      }
  }
  return undefined
}

function bookInlineBlocksToEditorBlocks(
  blocks: Extract<BookBlock, { type: 'text' | 'br' | 'code' | 'em' | 'a' }>[],
): Blocks {
  return blocks
    .map((block) => bookBlockToEditorBlock(block))
    .filter((block): block is Block => block !== undefined)
}

export const embeddingTextTransform: BlocksSerializeTransform = (block) => {
  let result: BlocksSerializeTransformResult
  if (block.type === 'cols' || block.type === 'col') result = block.children
  else if (
    block.type === 'pageMeta' ||
    block.type === 'hr' ||
    block.type === 'page' ||
    block.type === 'pageLink' ||
    block.type === 'pages' ||
    block.type === 'mention'
  )
    result = ' '
  else if (
    block.type === 'img' ||
    block.type === 'preview' ||
    block.type === 'video'
  )
    result = block.caption ?? null
  else if (block.type === 'audio') result = null
  else if (block.type === 'file') result = block.name
  return result
}

export const ftsTextTransform: BlocksSerializeTransform = (block) => {
  let result: BlocksSerializeTransformResult
  if (block.type === 'cols' || block.type === 'col') result = block.children
  else if (block.type === 'pageMeta' || block.type === 'hr') result = null
  else if (
    block.type === 'img' ||
    block.type === 'preview' ||
    block.type === 'video'
  )
    result = block.caption ?? (block as any).src
  else if (block.type === 'audio') result = block.src
  else if (block.type === 'file') result = block.name
  return result
}

function normalizeIndexText(text: string): string {
  return text
    .replaceAll(/[ \t]+/g, ' ')
    .replaceAll(/ *\n */g, '\n')
    .replaceAll(/\n{3,}/g, '\n\n')
    .replaceAll(/ +([,.;:!?])/g, '$1')
}

function applyTransform(
  block: Block,
  options: BlocksToMdOptions,
): AppliedTransform {
  if (!options.transform) return { type: 'default' }

  const result = options.transform(block)
  if (result === undefined || result === block) return { type: 'default' }
  if (result === null) return { type: 'omit' }
  if (typeof result === 'string') return { type: 'string', value: result }
  if (Array.isArray(result)) return { type: 'blocks', value: result }
  return { type: 'block', value: result }
}

function blocksToPlainText(blocks: Blocks, options: BlocksToMdOptions): string {
  return blocks
    .map((block) => blockToPlainText(block, options))
    .filter((text) => text.trim().length > 0)
    .join('\n\n')
}

function childBlocksToPlainText(
  blocks: Blocks,
  options: BlocksToMdOptions,
): string {
  return blocks
    .map((block) => blockToPlainText(block, options))
    .filter((text) => text.trim().length > 0)
    .join('\n')
}

function collectPlainText(parts: Array<string | undefined>): string {
  return parts.filter((part) => part && part.length > 0).join('\n')
}

function transformedToPlainText(
  transformed: AppliedTransform,
  options: BlocksToMdOptions,
): string | undefined {
  switch (transformed.type) {
    case 'default':
      return undefined
    case 'omit':
      return ''
    case 'string':
      return transformed.value
    case 'block':
      return blockToPlainText(transformed.value, options)
    case 'blocks':
      return blocksToPlainText(transformed.value, options)
  }
  return undefined
}

function blockToPlainText(block: Block, options: BlocksToMdOptions): string {
  const transformed = transformedToPlainText(
    applyTransform(block, options),
    options,
  )
  if (transformed !== undefined) return transformed

  // oxlint-disable-next-line typescript/switch-exhaustiveness-check -- unknown block types produce no text
  switch (block.type) {
    case 'text':
      return block.text
    case 'chars':
      return getTextFromChars(block)
    case 'br':
      return '\n'
    case 'code':
    case 'em':
    case 'l':
    case 'diff':
    case 'review':
    case 'a':
    case 'cloze':
    case 'p':
    case 'h':
    case 'li':
    case 'col':
      return inlineBlocksToPlainText(
        (block as { children: Blocks }).children,
        options,
      )
    case 'comment':
      return inlineBlocksToPlainText(block.children, options)
    case 'criticComment':
      return ''
    case 'quote':
      return collectPlainText([
        block.author
          ? inlineBlocksToPlainText(block.author, options)
          : undefined,
        childBlocksToPlainText(block.children, options),
      ])
    case 'alert':
      return collectPlainText([
        block.title,
        childBlocksToPlainText(block.children, options),
      ])
    case 'rule':
    case 'questionGroup':
      return childBlocksToPlainText(block.children, options)
    case 'cols':
      return childBlocksToPlainText(block.children, options)
    case 'list':
      return block.children
        .map((child) => blockToPlainText(child, options))
        .filter((text) => text.length > 0)
        .join('\n')
    case 'table':
      return unwrapReviewTableRows(block.children)
        .map((row) =>
          unwrapReviewTableCells(row.children)
            .map((cell) => inlineBlocksToPlainText(cell.children, options))
            .join('\t'),
        )
        .join('\n')
    case 'pre':
      return collectPlainText([
        inlineBlocksToPlainText(block.children, options).trimEnd(),
        block.caption && inlineBlocksToPlainText(block.caption, options),
      ])
    case 'pageTitle':
      return block.title
    case 'page':
      return block.pageId
    case 'pageLink':
      return block.pageId
    case 'pages':
      return block.ids.join('\n')
    case 'mention':
      if (block.subtype === 'date') return String(block.date)
      return block.$at ? `@${block.uid}` : block.uid
    case 'question':
      return questionToPlainText(block, options)
    case 'pageMeta':
      return typeof (block as any).title === 'string'
        ? (block as any).title
        : ''
    case 'img':
    case 'preview':
      return block.caption
        ? inlineBlocksToPlainText(block.caption, options)
        : block.src
    case 'video':
      return block.caption
        ? inlineBlocksToPlainText(block.caption, options)
        : block.src
    case 'audio':
      return block.src
    case 'file':
      return block.name
    case 'hr':
      return ''
  }
  return ''
}

function questionToPlainText(
  block: Block & { type: 'question' },
  options: BlocksToMdOptions,
): string {
  const question = block.question
    ? inlineBlocksToPlainText(block.question, options)
    : ''
  const opts =
    block.subtype === 'sc' || block.subtype === 'mc'
      ? block.opts
          .map((opt) => inlineBlocksToPlainText(opt, options))
          .join('\n')
      : ''
  const answer = Array.isArray((block as any).answer)
    ? ((block as any).answer as unknown[]).join('\n')
    : typeof (block as any).answer === 'string'
      ? (block as any).answer
      : typeof (block as any).answer?.text === 'string'
        ? (block as any).answer.text
        : ''
  const criteria = Array.isArray((block as any).criteria)
    ? ((block as any).criteria as unknown[]).join('\n')
    : ''
  const explanation = block.explanation
    ? inlineBlocksToPlainText(block.explanation, options)
    : ''
  const children = Array.isArray((block as any).children)
    ? childBlocksToPlainText((block as any).children, options)
    : ''
  return collectPlainText([
    question,
    opts,
    answer,
    criteria,
    explanation,
    children,
  ])
}

// Helper to extract plain text from inline blocks
function inlineBlocksToPlainText(
  blocks: Blocks,
  options: BlocksToMdOptions,
): string {
  let text = ''
  for (const block of blocks) {
    const transformed = transformedToPlainText(
      applyTransform(block, options),
      options,
    )
    if (transformed !== undefined) {
      text += transformed
    } else if (block.type === 'br') {
      text += '\n'
    } else if (block.type === 'text') {
      text += block.text
    } else if (block.type === 'chars') {
      text += getTextFromChars(block)
    } else if (block.type === 'page') {
      text += block.pageId
    } else if (block.type === 'pageLink') {
      text += block.pageId
    } else if (block.type === 'pages') {
      text += block.ids.join('\n')
    } else if (block.type === 'mention') {
      text += block.subtype === 'date' ? String(block.date) : block.uid
    } else if (block.type === 'file') {
      text += block.name
    } else if (
      (block.type === 'img' ||
        block.type === 'preview' ||
        block.type === 'video') &&
      block.caption
    ) {
      text += inlineBlocksToPlainText(block.caption, options)
    } else if (block.type === 'audio') {
      text += block.src
    } else if ('children' in block && Array.isArray(block.children)) {
      text += inlineBlocksToPlainText(block.children, options)
    }
  }
  return text
}

// Preserve inline runs among block children of question and explanation slots.
function mixedSlotToTokens(blocks: Blocks, level: number, options: BlocksToMdOptions): Token[] {
  const tokens: Token[] = []
  let run: Blocks = []
  const flush = () => {
    if (run.length === 0) return
    const open = new Token('directive_block_open', 'div', 1)
    open.markup = fenceForChildren(run)
    open.meta = { name: 'inline', attributes: {} }
    const close = new Token('directive_block_close', 'div', -1)
    close.markup = open.markup
    tokens.push(open, ...blockToTokens({ type: 'p', id: '', children: run }, level + 1, false, options), close)
    run = []
  }
  for (const block of blocks) {
    if (INLINE_BLOCK_TYPES.has(block.type)) run.push(block)
    else { flush(); tokens.push(...blockToTokens(block, level, false, options, tokens)) }
  }
  flush()
  return tokens
}

function blocksToTokens(
  blocks: Blocks,
  level: number,
  inList: boolean,
  options: BlocksToMdOptions,
  previousTokens: Token[] = [],
): Token[] {
  const tokens: Token[] = []

  for (const block of blocks) {
    const blockTokens = blockToTokens(
      block,
      level,
      inList,
      options,
      tokens.length > 0 ? tokens : previousTokens,
    )
    tokens.push(...blockTokens)
  }

  return tokens
}

function transformedToBlockTokens(
  transformed: AppliedTransform,
  level: number,
  inList: boolean,
  options: BlocksToMdOptions,
  previousTokens: Token[] = [],
): Token[] | undefined {
  switch (transformed.type) {
    case 'default':
      return undefined
    case 'omit':
      return []
    case 'string':
      return stringToBlockTokens(transformed.value, level, inList)
    case 'block':
      return blockToTokens(
        transformed.value,
        level,
        inList,
        options,
        previousTokens,
      )
    case 'blocks':
      return blocksToTokens(
        transformed.value,
        level,
        inList,
        options,
        previousTokens,
      )
  }
  return undefined
}

function stringToBlockTokens(
  text: string,
  level: number,
  inList: boolean,
): Token[] {
  if (text.length === 0) return []

  const openToken = new Token('paragraph_open', 'p', 1)
  openToken.level = level
  openToken.block = true
  openToken.hidden = inList

  const inlineToken = new Token('inline', '', 0)
  inlineToken.level = level + 1
  inlineToken.block = true
  inlineToken.children = stringToInlineTokens(text, true)
  inlineToken.content = ''

  const closeToken = new Token('paragraph_close', 'p', -1)
  closeToken.level = level
  closeToken.block = true
  closeToken.hidden = inList

  return [openToken, inlineToken, closeToken]
}

function rawStringToBlockTokens(text: string, level: number): Token[] {
  const openToken = new Token('paragraph_open', 'p', 1)
  openToken.level = level
  openToken.block = true

  const inlineToken = new Token('inline', '', 0)
  inlineToken.level = level + 1
  inlineToken.block = true
  const textToken = new Token('text', '', 0)
  textToken.content = text
  inlineToken.children = [textToken]

  const closeToken = new Token('paragraph_close', 'p', -1)
  closeToken.level = level
  closeToken.block = true
  return [openToken, inlineToken, closeToken]
}

function blockToTokens(
  block: Block,
  level: number = 0,
  inList: boolean = false,
  options: BlocksToMdOptions = {},
  previousTokens: Token[] = [],
): Token[] {
  const transformed = transformedToBlockTokens(
    applyTransform(block, options),
    level,
    inList,
    options,
    previousTokens,
  )
  if (transformed) return transformed

  const tokens: Token[] = []

  // oxlint-disable-next-line typescript/switch-exhaustiveness-check -- handles known block types; unknown types produce no tokens
  switch (block.type) {
    case 'h': {
      const openToken = new Token('heading_open', `h${block.level}`, 1)
      openToken.level = level
      openToken.block = true

      const inlineToken = new Token('inline', '', 0)
      inlineToken.level = level + 1
      inlineToken.block = true
      inlineToken.children = oneLineTokens(escapeInlineEdgeSpaces(
        inlineBlocksToTokens(block.children, true, options),
      ))
      inlineToken.content = ''

      const closeToken = new Token('heading_close', `h${block.level}`, -1)
      closeToken.level = level
      closeToken.block = true

      tokens.push(openToken, inlineToken, closeToken)
      break
    }

    case 'p': {
      if (block.align) {
        const { align, ...paragraph } = block
        const open = new Token('directive_block_open', 'div', 1)
        open.level = level
        open.block = true
        open.markup = ':::'
        open.meta = { name: 'paragraph', attributes: { align } }
        const close = new Token('directive_block_close', 'div', -1)
        close.level = level
        close.block = true
        close.markup = ':::'
        tokens.push(open, ...blockToTokens(paragraph, level + 1, inList, options, tokens), close)
        break
      }
      const openToken = new Token('paragraph_open', 'p', 1)
      openToken.level = level
      openToken.block = true
      // Mark paragraphs in tight lists as hidden
      openToken.hidden = inList

      const inlineToken = new Token('inline', '', 0)
      inlineToken.level = level + 1
      inlineToken.block = true
      const children = inlineBlocksToTokens(block.children, true, options)
      inlineToken.children = inList
        ? children
        : escapeInlineEdgeSpaces(children)
      inlineToken.content = ''

      const closeToken = new Token('paragraph_close', 'p', -1)
      closeToken.level = level
      closeToken.block = true
      closeToken.hidden = inList

      tokens.push(openToken, inlineToken, closeToken)
      break
    }

    case 'definition': {
      const token = new Token('html_block', '', 0)
      token.level = level
      token.block = true
      const raw = inlineBlocksToPlainText(block.children, options)
      if (/^ {0,3}\[(?:\^)?[^\]]+\]:/.test(raw)) token.content = raw + '\n'
      else {
        const open = new Token('directive_block_open', 'div', 1)
        open.markup = ':::'
        open.meta = { name: 'definition', attributes: { kind: block.kind } }
        const close = new Token('directive_block_close', 'div', -1)
        close.markup = ':::'
        tokens.push(open, ...blockToTokens({ type: 'p', id: '', children: block.children }, level + 1, false, options), close)
        break
      }
      tokens.push(token)
      break
    }

    case 'list': {
      const isToggle = block.subtype === 'toggle'
      const explicitSpread = block.spread === true && block.children.length < 2
      const wrapped = isToggle || explicitSpread
      const listType = block.subtype === 'ordered' ? 'ordered' : 'bullet'
      // Parent open tokens and non-list siblings reset the marker;
      // omitted blocks leave the previous emitted sibling intact.
      const previousToken = previousTokens.at(-1)
      const previousList =
        previousToken?.type === 'directive_block_close' &&
        previousToken.meta?.name === 'toggle'
          ? previousTokens.at(-2)
          : previousToken
      const previousMarkup =
        previousList?.type === `${listType}_list_close` ? previousList.markup : ''
      const markup =
        listType === 'ordered'
          ? previousMarkup === '.'
            ? ')'
            : '.'
          : previousMarkup === '-'
            ? '*'
            : '-'

      // Wrap toggle lists in directive
      const toggleFence = wrapped ? fenceForChildren(block.children) : ':::'
      if (wrapped) {
        const directiveOpen = new Token('directive_block_open', 'div', 1)
        directiveOpen.level = level
        directiveOpen.block = true
        directiveOpen.markup = toggleFence
        directiveOpen.meta = {
          name: isToggle ? 'toggle' : 'list',
          attributes: explicitSpread ? { spread: 'true' } : {},
        }
        tokens.push(directiveOpen)
      }

      const openToken = new Token(
        `${listType}_list_open`,
        listType === 'ordered' ? 'ol' : 'ul',
        1,
      )
      openToken.markup = markup
      openToken.level = wrapped ? level + 1 : level
      openToken.block = true
      tokens.push(openToken)

      // Track item number for ordered lists
      let itemNumber = block.start ?? 1
      const childLevel = wrapped ? level + 2 : level + 1
      for (const child of block.children) {
        if (block.spread && itemNumber > (block.start ?? 1)) {
          tokens.push(new Token('list_item_separator', '', 0))
        }
        const childTokens = blockToTokens(child, childLevel, true, options, tokens) // Pass inList=true
        // Set markup and item number on direct child list_item tokens only
        // (not nested list items which have different level)
        if (child.type === 'li') {
          for (const token of childTokens) {
            if (token.type === 'list_item_open' && token.level === childLevel) {
              token.markup = markup
              if (listType === 'ordered') {
                token.info = String(itemNumber)
              }
            } else if (
              token.type === 'list_item_close' &&
              token.level === childLevel
            ) {
              token.markup = markup
            }
          }
          itemNumber++
        }
        tokens.push(...childTokens)
      }

      const closeToken = new Token(
        `${listType}_list_close`,
        listType === 'ordered' ? 'ol' : 'ul',
        -1,
      )
      closeToken.markup = markup
      closeToken.level = wrapped ? level + 1 : level
      closeToken.block = true
      tokens.push(closeToken)

      // Close toggle directive
      if (wrapped) {
        const directiveClose = new Token('directive_block_close', 'div', -1)
        directiveClose.level = level
        directiveClose.block = true
        directiveClose.markup = toggleFence
        tokens.push(directiveClose)
      }

      break
    }

    case 'li': {
      const openToken = new Token('list_item_open', 'li', 1)
      openToken.markup = '-' // will be set by parent list
      openToken.level = level
      openToken.block = true

      if (block.done !== undefined) {
        openToken.meta = { task: true, taskChecked: block.done }
        openToken.attrSet('data-task', 'true')
        openToken.attrSet('data-task-checked', String(block.done))
      }

      tokens.push(openToken)

      // mdast-util-to-markdown/lib/handle/list-item.js:49-66: render the body independently, then indent every continuation.
      const content = new Token('list_item_content', '', 0)
      content.content = listItemContent(block.children, options)
      tokens.push(content)

      const closeToken = new Token('list_item_close', 'li', -1)
      closeToken.markup = '-' // will be set by parent list
      closeToken.level = level
      closeToken.block = true
      tokens.push(closeToken)
      break
    }

    case 'hr': {
      const token = new Token('hr', 'hr', 0)
      token.markup = '---'
      token.level = level
      token.block = true
      tokens.push(token)
      break
    }

    case 'pre': {
      if (block.latex) {
        const token = new Token('math_block', '', 0)
        token.level = level
        token.block = true
        token.content = inlineBlocksToPlainText(block.children, options)
        tokens.push(token)
      } else {
        const token = new Token('fence', 'code', 0)
        token.level = level
        token.block = true
        token.info = block.lang ?? ''
        const attributes: string[] = []
        if (block.lines !== undefined) attributes.push(`lines:=${JSON.stringify(JSON.stringify(block.lines)).replaceAll('\\', '\\\\')}`)
        if (block.caption) {
          const caption = attributeMarkdown(block.caption, options)
          const assignment = /["\n{}]/.test(caption) ? `caption:=${JSON.stringify(caption)}` : `caption="${caption}"`
          const inline = block.caption.every(child => INLINE_BLOCK_TYPES.has(child.type))
          attributes.push(`${assignment}${inline ? ' captionInline="true"' : ''}`)
          // Backtick fences reject backticks in their info string.
          if (caption.includes('`')) token.markup = '~~~'
        }
        if (attributes.length > 0) token.info += `${token.info ? ' ' : ''}{${attributes.join(' ')}}`
        const content = isDiffCodeBlock(block)
          ? block.children
              .filter(
                (child): child is Extract<Block, { type: 'diff' }> =>
                  child.type === 'diff',
              )
              .map(
                (line) =>
                  `${diffMarker(line.kind)}${inlineBlocksToPlainText(line.children, options)}`,
              )
              .join('\n')
          : inlineBlocksToPlainText(block.children, options)
        token.content = content === '' && block.children.length === 0 ? '' : `${content}\n`
        tokens.push(token)
      }
      break
    }

    case 'diff': {
      const openToken = new Token('paragraph_open', 'p', 1)
      openToken.level = level
      openToken.block = true

      const inlineToken = new Token('inline', '', 0)
      inlineToken.level = level + 1
      inlineToken.block = true
      inlineToken.children = inlineBlockToTokens(block, true, options)

      const closeToken = new Token('paragraph_close', 'p', -1)
      closeToken.level = level
      closeToken.block = true

      tokens.push(openToken, inlineToken, closeToken)
      break
    }

    case 'review': {
      if (block.display === 'block') {
        const fence = fenceForChildren(block.children)
        const openToken = new Token('directive_block_open', 'div', 1)
        openToken.level = level
        openToken.block = true
        openToken.markup = fence
        openToken.meta = {
          name: 'review',
          attributes: { change: block.change, state: block.state },
        }
        tokens.push(openToken)
        for (const child of block.children) {
          tokens.push(...blockToTokens(child, level + 1, inList, options, tokens))
        }
        const closeToken = new Token('directive_block_close', 'div', -1)
        closeToken.level = level
        closeToken.block = true
        closeToken.markup = fence
        tokens.push(closeToken)
        break
      }
      const openToken = new Token('paragraph_open', 'p', 1)
      openToken.level = level
      openToken.block = true
      const inlineToken = new Token('inline', '', 0)
      inlineToken.level = level + 1
      inlineToken.block = true
      inlineToken.children = reviewToInlineTokens(block, options)
      const closeToken = new Token('paragraph_close', 'p', -1)
      closeToken.level = level
      closeToken.block = true
      tokens.push(openToken, inlineToken, closeToken)
      break
    }

    case 'comments': {
      const openToken = new Token('directive_block_open', 'div', 1)
      openToken.level = level
      openToken.block = true
      openToken.markup = ':::'
      openToken.meta = { name: 'comments', attributes: {} }

      const jsonTokens = rawStringToBlockTokens(
        JSON.stringify(stripRuntimeState(block.threads)),
        level + 1,
      )

      const closeToken = new Token('directive_block_close', 'div', -1)
      closeToken.level = level
      closeToken.block = true
      closeToken.markup = ':::'
      tokens.push(openToken, ...jsonTokens, closeToken)
      break
    }

    case 'table': {
      // A cell is an inline-level slot: a document stored before that rule saves its cells' inline content.
      const table = cellInlineTable(block, () => '')
      if (!pipeTable(table, options)) {
        const token = new Token('html_block', '', 0)
        token.block = true
        token.level = level
        token.content = tableToHtml(table, (children) => {
          const markdown = (content: Blocks) => defaultMarkdown.renderer.renderInline(escapeInlineEdgeSpaces(inlineBlocksToTokens(content, true, options)), {}, {})
          if (children.some(child => child.type === 'chars' && ((child.format ?? 0) & FormatFlag.CODE) !== 0 && ((child.format ?? 0) & ~FormatFlag.CODE) !== 0)) {
            return `<md-inline>${markdown(children).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('\n', '&#10;')}</md-inline>`
          }
          return blocksToTableHtml(children, markdown)
        }) + '\n\n'
        tokens.push(token)
        break
      }
      const openToken = new Token('table_open', 'table', 1)
      openToken.level = level
      openToken.block = true

      // Preserve table metadata for round-trip
      const tableBlock = table as any
      if (tableBlock._aligns || tableBlock._colWidths) {
        openToken.meta = {
          ...(tableBlock._aligns && { aligns: tableBlock._aligns }),
          ...(tableBlock._colWidths && { colWidths: tableBlock._colWidths }),
        }
      }

      tokens.push(openToken)

      // Check if first row has header cells
      const rows = table.children as any[]
      const hasHeader =
        rows.length > 0 && rows[0].children?.some((cell: any) => cell._isHeader)

      if (hasHeader) {
        // Emit thead with first row
        const theadOpen = new Token('thead_open', 'thead', 1)
        theadOpen.level = level + 1
        theadOpen.block = true
        tokens.push(
          theadOpen,
          ...blockToTokens(rows[0], level + 2, false, options, tokens),
        )

        const theadClose = new Token('thead_close', 'thead', -1)
        theadClose.level = level + 1
        theadClose.block = true
        tokens.push(theadClose)

        // Emit tbody with remaining rows
        if (rows.length > 1) {
          const tbodyOpen = new Token('tbody_open', 'tbody', 1)
          tbodyOpen.level = level + 1
          tbodyOpen.block = true
          tokens.push(tbodyOpen)

          for (let i = 1; i < rows.length; i++) {
            tokens.push(...blockToTokens(rows[i], level + 2, false, options, tokens))
          }

          const tbodyClose = new Token('tbody_close', 'tbody', -1)
          tbodyClose.level = level + 1
          tbodyClose.block = true
          tokens.push(tbodyClose)
        }
      } else {
        // No header, just emit all rows
        for (const row of rows) {
          tokens.push(...blockToTokens(row, level + 1, false, options, tokens))
        }
      }

      const closeToken = new Token('table_close', 'table', -1)
      closeToken.level = level
      closeToken.block = true
      tokens.push(closeToken)
      break
    }

    case 'tr': {
      const openToken = new Token('tr_open', 'tr', 1)
      openToken.level = level
      openToken.block = true
      tokens.push(openToken)

      for (const cell of block.children) {
        const cellData = cell as any
        const isHeader = cellData._isHeader
        const cellType = isHeader ? 'th' : 'td'

        const cellOpen = new Token(`${cellType}_open`, cellType, 1)
        cellOpen.level = level + 1
        cellOpen.block = true

        const cellInline = new Token('inline', '', 0)
        cellInline.level = level + 2
        cellInline.block = true
        cellInline.children = escapeInlineEdgeSpaces(
          inlineBlocksToTokens(cell.children, true, options),
        )
        cellInline.content = ''

        const cellClose = new Token(`${cellType}_close`, cellType, -1)
        cellClose.level = level + 1
        cellClose.block = true

        tokens.push(cellOpen, cellInline, cellClose)
      }

      const closeToken = new Token('tr_close', 'tr', -1)
      closeToken.level = level
      closeToken.block = true
      tokens.push(closeToken)
      break
    }

    case 'quote': {
      // Check if this is a directive blockquote (with author)
      if (block.author) {
        const fence = fenceForChildren(block.children)
        const openToken = new Token('directive_block_open', 'div', 1)
        openToken.level = level
        openToken.block = true
        openToken.markup = fence

        const attributes: Record<string, string> = { author: attributeInline(block.author, options) }

        openToken.meta = {
          name: 'blockquote',
          attributes,
        }
        tokens.push(openToken)

        for (const child of block.children) {
          tokens.push(...blockToTokens(child, level + 1, false, options, tokens))
        }

        const closeToken = new Token('directive_block_close', 'div', -1)
        closeToken.level = level
        closeToken.block = true
        closeToken.markup = fence
        tokens.push(closeToken)
      } else {
        // Plain blockquote
        const openToken = new Token('blockquote_open', 'blockquote', 1)
        openToken.level = level
        openToken.block = true
        tokens.push(openToken)

        for (const child of block.children) {
          tokens.push(...blockToTokens(child, level + 1, false, options, tokens))
        }

        const closeToken = new Token('blockquote_close', 'blockquote', -1)
        closeToken.level = level
        closeToken.block = true
        tokens.push(closeToken)
      }
      break
    }

    case 'alert': {
      const fence = fenceForChildren(block.children)
      const openToken = new Token('directive_block_open', 'div', 1)
      openToken.level = level
      openToken.block = true
      openToken.markup = fence

      const attributes: Record<string, string> = {}
      if (block.icon) {
        attributes.icon = block.icon
      }
      if (block.color) {
        attributes.color = block.color
      }
      if (block.title) {
        attributes.title = block.title
      }
      if (block.collapsable !== undefined) {
        attributes.collapsable = String(block.collapsable)
      }

      openToken.meta = {
        name: 'alert',
        attributes,
      }
      tokens.push(openToken)

      for (const child of block.children) {
        tokens.push(...blockToTokens(child, level + 1, inList, options, tokens))
      }

      const closeToken = new Token('directive_block_close', 'div', -1)
      closeToken.level = level
      closeToken.block = true
      closeToken.markup = fence
      tokens.push(closeToken)
      break
    }

    case 'rule': {
      const fence = fenceForChildren(block.children)
      const openToken = new Token('directive_block_open', 'div', 1)
      openToken.level = level
      openToken.block = true
      openToken.markup = fence

      const attributes: Record<string, string> = {
        key: block.key,
        language: block.language,
        scope: block.scope,
      }
      if (block.predicts.length > 0)
        attributes.predicts = block.predicts.join(',')
      // The rule's card identity (qid) has no other markdown syntax; keep it so a rule page round-trips as .md.
      if (block.qid) attributes.qid = block.qid
      if (block.affix) {
        attributes.affixKind = block.affix.kind
        attributes.affixForm = block.affix.form
        attributes.affixMeaning = block.affix.meaning
      }

      openToken.meta = { name: 'rule', attributes }
      tokens.push(openToken)
      for (const child of block.children) {
        tokens.push(...blockToTokens(child, level + 1, inList, options, tokens))
      }
      const closeToken = new Token('directive_block_close', 'div', -1)
      closeToken.level = level
      closeToken.block = true
      closeToken.markup = fence
      tokens.push(closeToken)
      break
    }

    case 'questionGroup': {
      const fence = fenceForChildren(block.children)
      const openToken = new Token('directive_block_open', 'div', 1)
      openToken.level = level
      openToken.block = true
      openToken.markup = fence
      openToken.meta = { name: 'question-group', attributes: {} }
      tokens.push(openToken)
      for (const child of block.children) {
        tokens.push(...blockToTokens(child, level + 1, inList, options, tokens))
      }
      const closeToken = new Token('directive_block_close', 'div', -1)
      closeToken.level = level
      closeToken.block = true
      closeToken.markup = fence
      tokens.push(closeToken)
      break
    }

    case 'cols': {
      const fence = fenceForChildren(block.children)
      const openToken = new Token('directive_block_open', 'div', 1)
      openToken.level = level
      openToken.block = true
      openToken.markup = fence
      const attributes: Record<string, string> = {}
      openToken.meta = {
        name: 'cols',
        attributes,
      }
      tokens.push(openToken)

      for (const child of block.children) {
        tokens.push(...blockToTokens(child, level + 1, inList, options, tokens))
      }

      const closeToken = new Token('directive_block_close', 'div', -1)
      closeToken.level = level
      closeToken.block = true
      closeToken.markup = fence
      tokens.push(closeToken)
      break
    }

    case 'col': {
      const fence = fenceForChildren(block.children)
      const openToken = new Token('directive_block_open', 'div', 1)
      openToken.level = level
      openToken.block = true
      openToken.markup = fence

      const attributes: Record<string, string> = {}
      if (block.width) attributes.width = percentWidth(block.width)

      openToken.meta = {
        name: 'col',
        attributes,
      }
      tokens.push(openToken)

      for (const child of block.children) {
        tokens.push(...blockToTokens(child, level + 1, inList, options, tokens))
      }

      const closeToken = new Token('directive_block_close', 'div', -1)
      closeToken.level = level
      closeToken.block = true
      closeToken.markup = fence
      tokens.push(closeToken)
      break
    }

    case 'question': {
      // Check if this is an inline question
      if ((block as any).inline) {
        // Serialize as inline directive in paragraph
        const pOpen = new Token('paragraph_open', 'p', 1)
        pOpen.level = level
        pOpen.block = true
        tokens.push(pOpen)

        const inlineToken = new Token('inline', '', 0)
        inlineToken.level = level + 1
        inlineToken.block = true
        inlineToken.content = ''

        const subtype = block.subtype
        const attributes: Record<string, string> = {}
        if (block.qid) attributes.qid = block.qid
        if ((block as any).fractionalIndex)
          attributes.fractionalIndex = (block as any).fractionalIndex
        addPromptMetadataAttributes(attributes, block)

        const answer = (block as any).answer
        if (subtype === 'sa') {
          attributes.answer = JSON.stringify(answer).replaceAll('"', "'")
          if ((block as any).strict) attributes.class = 'strict'
          if ((block as any).answerRegexp)
            attributes.answerRegexp = (block as any).answerRegexp
        } else if (subtype === 'sc' || subtype === 'mc') {
          const opts = block.opts.map((option) => blocksToMd(option, options))
          attributes.opts = JSON.stringify(opts).replaceAll('"', "'")
          attributes.answer =
            subtype === 'sc' ? String(answer) : JSON.stringify(answer)
        }

        if ((block as any).explanation) {
          const mdParser = defaultMarkdown
          const explTokens = blocksToTokens(
            (block as any).explanation,
            0,
            false,
            options,
          )
          const explText = mdParser.renderer.render(explTokens, {}, {})
          attributes.explanation = explText.trim()
        }

        const directiveToken = new Token('directive_inline', 'span', 0)
        directiveToken.markup = '::'
        directiveToken.meta = {
          name: subtype,
          content: '',
          attributes,
        }
        inlineToken.children = [directiveToken]
        tokens.push(inlineToken)

        const pClose = new Token('paragraph_close', 'p', -1)
        pClose.level = level
        pClose.block = true
        tokens.push(pClose)

        break
      }

      // Check if this is a question with subtype (la, sa, sc, mc, sr)
      const subtype = (block as any).subtype

      if (subtype) {
        const questionBlock = (block as any).question as Blocks | undefined
        const blockPrompt = hasBlockPrompt(block)
        const containerSlots = questionContainerSlots(block)
        const outerFence = containerSlots.length > 0
          ? ':'.repeat(Math.max(...containerSlots.map(slot => slotFence(slot).length)) + 1)
          : ':::'
        // Question with subtype: :::la...:::
        const openToken = new Token('directive_block_open', 'div', 1)
        openToken.level = level
        openToken.block = true
        openToken.markup = outerFence

        const attributes: Record<string, string> = {}
        if (block.qid) {
          attributes.qid = block.qid
        }
        if ((block as any).fractionalIndex) {
          attributes.fractionalIndex = (block as any).fractionalIndex
        }
        addCriteriaAttribute(attributes, block)
        addSpokenResponseAnswerAttributes(attributes, block)
        addPromptMetadataAttributes(attributes, block)
        if (subtype === 'sa' && (block as any).strict)
          attributes.class = 'strict'
        if (subtype === 'sa' && (block as any).answerRegexp)
          attributes.answerRegexp = (block as any).answerRegexp

        openToken.meta = {
          name: subtype,
          attributes,
        }
        tokens.push(openToken)

        // Add inner question. Inline-only prompts keep compact bracket syntax;
        // empty and media/code/layout prompts use a container directive.
        if (questionBlock) {
          const questionOpen = new Token('directive_block_open', 'div', 1)
          questionOpen.level = level + 1
          questionOpen.block = true
          questionOpen.markup = blockPrompt ? slotFence(questionBlock) : fenceForChildren(questionBlock)
          if (blockPrompt) {
            questionOpen.meta = { name: 'question', attributes: {} }
            tokens.push(
              questionOpen,
              ...mixedSlotToTokens(questionBlock, level + 2, options),
            )
            const questionClose = new Token('directive_block_close', 'div', -1)
            questionClose.level = level + 1
            questionClose.block = true
            questionClose.markup = slotFence(questionBlock)
            tokens.push(questionClose)
          } else {
            const questionText = oneLineInline(inlineBlocksToTokens(questionBlock, true, options))
            questionOpen.meta = {
              name: 'question',
              attributes: {},
              content: questionText,
            }
            questionOpen.nesting = 0
            tokens.push(questionOpen)
          }
        }

        // Add one ::answer[text] line per accepted answer for sa questions.
        const answer = (block as any).answer
        if (
          subtype === 'sa' &&
          answer &&
          Array.isArray(answer) &&
          answer.length > 0
        ) {
          for (const answerText of answer) {
            const pOpen = new Token('paragraph_open', 'p', 1)
            pOpen.level = level + 1
            pOpen.block = true
            tokens.push(pOpen)

            const inlineToken = new Token('inline', '', 0)
            inlineToken.level = level + 2
            inlineToken.block = true
            inlineToken.content = ''

            const answerToken = new Token('directive_inline', 'span', 0)
            answerToken.markup = '::'
            answerToken.meta = {
              name: 'answer',
              content: answerText,
              attributes: {},
            }
            inlineToken.children = [answerToken]
            tokens.push(inlineToken)

            const pClose = new Token('paragraph_close', 'p', -1)
            pClose.level = level + 1
            pClose.block = true
            tokens.push(pClose)
          }
        }

        // Add ::opt directives for sc/mc questions
        if (block.subtype === 'sc' || block.subtype === 'mc') {
          for (let i = 0; i < block.opts.length; i++) {
            const opt = block.opts[i]

            // Check if this option is correct (answer contains this index)
            const answer = (block as any).answer
            let isCorrect = false
            if (answer !== undefined) {
              if (typeof answer === 'number') {
                isCorrect = answer === i
              } else if (Array.isArray(answer)) {
                isCorrect = answer.includes(i)
              }
            }

            const pOpen = new Token('paragraph_open', 'p', 1)
            pOpen.level = level + 1
            pOpen.block = true
            tokens.push(pOpen)

            const inlineToken = new Token('inline', '', 0)
            inlineToken.level = level + 2
            inlineToken.block = true
            inlineToken.content = ''

            const optToken = new Token('directive_inline', 'span', 0)
            optToken.markup = '::'

            const optText = oneLineInline(inlineBlocksToTokens(opt, true, options))

            const attributes: Record<string, any> = {}
            if (isCorrect) {
              attributes.class = 'correct'
            }

            optToken.meta = {
              name: 'opt',
              content: optText,
              attributes,
            }
            inlineToken.children = [optToken]
            tokens.push(inlineToken)

            const pClose = new Token('paragraph_close', 'p', -1)
            pClose.level = level + 1
            pClose.block = true
            tokens.push(pClose)
          }
        }

        // Add ::explanation[text]
        const explanationBlocks = (block as any).explanation
        if (explanationBlocks && hasBlockExplanation(block)) {
          const fence = slotFence(explanationBlocks)
          const explanationOpen = new Token('directive_block_open', 'div', 1)
          explanationOpen.level = level + 1
          explanationOpen.block = true
          explanationOpen.markup = fence
          explanationOpen.meta = { name: 'explanation', attributes: {} }
          tokens.push(explanationOpen, ...mixedSlotToTokens(explanationBlocks, level + 2, options))
          const explanationClose = new Token('directive_block_close', 'div', -1)
          explanationClose.level = level + 1
          explanationClose.block = true
          explanationClose.markup = fence
          tokens.push(explanationClose)
        } else if (explanationBlocks) {
          const pOpen = new Token('paragraph_open', 'p', 1)
          pOpen.level = level + 1
          pOpen.block = true
          tokens.push(pOpen)

          const inlineToken = new Token('inline', '', 0)
          inlineToken.level = level + 2
          inlineToken.block = true
          inlineToken.content = ''

          const explToken = new Token('directive_inline', 'span', 0)
          explToken.markup = '::'

          const explText = oneLineInline(inlineBlocksToTokens(explanationBlocks, true, options))

          explToken.meta = {
            name: 'explanation',
            content: explText,
            attributes: {},
          }
          inlineToken.children = [explToken]
          tokens.push(inlineToken)

          const pClose = new Token('paragraph_close', 'p', -1)
          pClose.level = level + 1
          pClose.block = true
          tokens.push(pClose)
        }

        const closeToken = new Token('directive_block_close', 'div', -1)
        closeToken.level = level
        closeToken.block = true
        closeToken.markup = outerFence
        tokens.push(closeToken)
      } else {
        // Simple question: :::question{...}...:::
        const openToken = new Token('directive_block_open', 'div', 1)
        openToken.level = level
        openToken.block = true
        openToken.markup = ':::'

        const attributes: Record<string, string> = {}
        if (block.qid) {
          attributes.qid = block.qid
        }
        addPromptMetadataAttributes(attributes, block)

        openToken.meta = {
          name: 'question',
          attributes,
        }
        tokens.push(openToken)

        for (const child of (block as any).children ?? []) {
          tokens.push(...blockToTokens(child, level + 1, inList, options, tokens))
        }

        const closeToken = new Token('directive_block_close', 'div', -1)
        closeToken.level = level
        closeToken.block = true
        closeToken.markup = ':::'
        tokens.push(closeToken)
      }
      break
    }

    case 'pageMeta': {
      // oxlint-disable-next-line no-unused-vars -- needed for destructuring
      const { type, ...rest } = stripRuntimeState(block)
      const token = new Token('front_matter', 'div', 0)
      token.level = level
      token.block = true
      token.markup = '---'
      const ordered = pageMetaOrder(rest)
      token.content = `${dumpFlatYaml(ordered)}
`
      token.meta = { type: 'yaml', data: ordered }
      tokens.push(token)
      break
    }

    case 'img':
    case 'preview': {
      // Wrap in paragraph to create block-level ctx
      const openToken = new Token('paragraph_open', 'p', 1)
      openToken.level = level
      openToken.block = true

      const inlineToken = new Token('inline', '', 0)
      inlineToken.level = level + 1
      inlineToken.block = true
      inlineToken.children = inlineBlockToTokens(block, false, options)
      inlineToken.content = ''

      const closeToken = new Token('paragraph_close', 'p', -1)
      closeToken.level = level
      closeToken.block = true

      tokens.push(openToken, inlineToken, closeToken)
      break
    }

    case 'file':
    case 'audio':
    case 'video':
    case 'page':
    case 'pageLink':
    case 'pages': {
      if (block.type === 'pageLink' || block.type === 'pages') {
        const open = new Token('directive_block_open', 'div', 1)
        open.level = level
        open.block = true
        open.markup = ':::'
        open.meta = { name: block.type, attributes: block.type === 'pageLink' ? { pageId: block.pageId, ...(block.spaceId === undefined ? {} : { spaceId: block.spaceId }) } : { ids: block.ids.join(',') } }
        const close = new Token('directive_block_close', 'div', -1)
        close.level = level
        close.block = true
        close.markup = ':::'
        tokens.push(open, close)
        break
      }
      // Wrap in paragraph for block-level ctx
      const openToken = new Token('paragraph_open', 'p', 1)
      openToken.level = level
      openToken.block = true

      const inlineToken = new Token('inline', '', 0)
      inlineToken.level = level + 1
      inlineToken.block = true
      inlineToken.children = inlineBlockToTokens(block, false, options)
      inlineToken.content = ''

      const closeToken = new Token('paragraph_close', 'p', -1)
      closeToken.level = level
      closeToken.block = true

      tokens.push(openToken, inlineToken, closeToken)
      break
    }

    // Add more cases as needed
  }

  return tokens
}

/**
 * A GFM pipe table holds exactly: one full header row (its delimiter row) over plain rows of the same width, no spans,
 * and cells whose content is one line of inline Markdown without `|`. Any other table is HTML, whose cells hold
 * everything.
 */
function pipeTable(table: ExtractBlock<'table'>, options: BlocksToMdOptions): boolean {
  const [head, ...body] = table.children
  if (head?.type !== 'tr' || head.children.length === 0 || tableHasSpans(table)) return false
  const width = head.children.length
  const plain = (cell: Block, header: boolean) => cell.type === 'td' &&
    Boolean('_isHeader' in cell && cell._isHeader) === header &&
    cell.children.every((child) => child.type !== 'br' && INLINE_BLOCK_TYPES.has(child.type)) &&
    !/[|\n]/.test(defaultMarkdown.renderer.renderInline(
      escapeInlineEdgeSpaces(inlineBlocksToTokens(cell.children, true, options)), {}, {}))
  return head.children.every((cell) => plain(cell, true)) && body.every((row) =>
    row.type === 'tr' && row.children.length === width && row.children.every((cell) => plain(cell, false)))
}

function inlineBlocksToTokens(
  blocks: Blocks,
  shouldEscape: boolean = true,
  options: BlocksToMdOptions = {},
): Token[] {
  const tokens: Token[] = []

  let runs: InlineFormatRun[] = []
  const flush = () => {
    tokens.push(...formatRunsToTokens(runs, shouldEscape, 0, textEscape(options)))
    runs = []
  }
  for (const block of blocks) {
    const transformed = applyTransform(block, options)
    if (block.type === 'chars' && transformed.type === 'default' && !block.escaped) {
      const previous = runs.at(-1)
      const format = block.format ?? 0
      const text = getTextFromChars(block)
      if (previous?.format === format && previous.escaped === block.escaped) {
        previous.text += text
      } else if (text) {
        runs.push({ text, format, escaped: block.escaped })
      }
    } else {
      flush()
      tokens.push(...inlineBlockToTokens(block, shouldEscape, options, transformed))
    }
  }
  flush()

  for (const index of [0, tokens.length - 1]) {
    if (tokens[index]?.type !== 'hardbreak') continue
    const token = new Token('directive_inline', 'span', 0)
    token.meta = { name: 'br', content: '', attributes: { break: true } }
    tokens[index] = token
  }
  return escapeFormatFlanking(tokens)
}

type InlineFormatRun = {
  text: string
  format: number
  escaped?: boolean
}

// Escaped is a persistent mark independent of CODE. Backticks cannot encode it.
function escapedTextToken(text: string, format = 0): Token {
  const token = new Token('directive_inline', 'span', 0)
  token.markup = '::'
  token.meta = {
    name: 'escaped',
    // Entities keep literal syntax, bracket balance, edge spaces and newlines.
    content: text.replaceAll(/[^\p{L}\p{N}]/gu, char => `&#${char.codePointAt(0)};`),
    attributes: format ? { format } : {},
  }
  return token
}

// Group by the shared outer prefix: each recursive level opens once for all
// consecutive runs carrying that bit, then closes before the prefix changes.
function formatRunsToTokens(
  runs: InlineFormatRun[],
  shouldEscape: boolean,
  depth = 0,
  escape = escapeMarkdown,
): Token[] {
  runs = runs.filter((run) => run.text.length > 0)
  const order = [
    FormatFlag.STRIKE,
    FormatFlag.BOLD,
    FormatFlag.ITALIC,
    FormatFlag.UNDERLINE,
  ]
  const bit = order[depth]
  if (bit === undefined) {
    return runs.map((run) => {
      const code = !!(run.format & FormatFlag.CODE)
      const token = new Token(code ? 'code_inline' : 'text', code ? 'code' : '', 0)
      token.content = code || !shouldEscape ? run.text : escape(run.text)
      return token
    })
  }
  const tokens: Token[] = []
  for (let start = 0; start < runs.length; ) {
    const enabled = !!(runs[start].format & bit)
    let end = start + 1
    while (end < runs.length && !!(runs[end].format & bit) === enabled) end++
    const children = formatRunsToTokens(
      runs.slice(start, end),
      shouldEscape,
      depth + 1,
      escape,
    )
    if (!enabled) tokens.push(...children)
    else if (bit === FormatFlag.UNDERLINE) {
      const token = new Token('directive_inline', 'span', 0)
      token.markup = '::'
      token.meta = {
        name: 'u',
        content: oneLineInline(children),
        attributes: {},
      }
      tokens.push(token)
    } else {
      const name =
        bit === FormatFlag.STRIKE ? 's' : bit === FormatFlag.BOLD ? 'strong' : 'em'
      const open = new Token(`${name}_open`, name, 1)
      const close = new Token(`${name}_close`, name, -1)
      if (bit === FormatFlag.ITALIC) {
        open.markup = '_'
        close.markup = open.markup
      }
      tokens.push(open, ...children, close)
    }
    start = end
  }
  return escapeInlineBoundarySpaces(tokens, -1, -1)
}

// CommonMark treats punctuation next to a delimiter differently from letters.
// Encode the adjacent outside letter when nested syntax would block flanking.
function escapeFormatFlanking(tokens: Token[]): Token[] {
  let boldDepth = 0
  const italicSpans: Array<{ index: number, bold: boolean }> = []
  for (const [index, token] of tokens.entries()) {
    if (token.type === 'strong_open') boldDepth++
    if (token.type === 'strong_close') boldDepth--
    if (token.type === 'em_open') {
      italicSpans.push({ index, bold: boldDepth > 0 })
    } else if (token.type === 'em_close') {
      const span = italicSpans.pop()
      if (!span) continue
      // Keep standalone italics as *, but avoid adjoining bold's ** marker.
      const adjacentBold =
        tokens[span.index - 1]?.type === 'strong_close' ||
        tokens[index + 1]?.type === 'strong_open'
      const markup = span.bold || adjacentBold ? '_' : '*'
      tokens[span.index].markup = markup
      token.markup = markup
    }
  }
  const renderer = defaultMarkdown.renderer
  const rendered = tokens.map((token) => renderer.renderInline([token], {}, {}))
  const punctuation = /[\p{P}\p{S}]/u
  const letter = /[\p{L}\p{N}]/u
  // Encoding a one-character text token as `&#N;` also turns its other edge into punctuation (`&` or `;`), which can
  // stop the delimiter on that side from flanking (CommonMark 0.31.2 §6.2, left-flanking (2b): a run followed by
  // punctuation must be preceded by whitespace or punctuation; rule 5: `**` opens only when left-flanking). That
  // delimiter may sit before the one being fixed, so the pass repeats until nothing changes: `cc**a_b_**` needs
  // `c&#99;**&#97;_b_**`. Each repeat encodes at least one more letter, so it ends.
  for (let changed = true; changed; ) {
    changed = false
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]
      if (!isFormatDelimiter(token, 1) && !isFormatDelimiter(token, -1)) continue
      const before = rendered[i - 1]?.at(-1) ?? ''
      const after = rendered[i + 1]?.[0] ?? ''
      const underscore = token.markup === '_'
      const followsSpace =
        tokens[i - 1]?.type === 'html_inline' && /^(&#32;|&#10;)+$/u.test(rendered[i - 1])
      const precedesSpace =
        tokens[i + 1]?.type === 'html_inline' && /^(&#32;|&#10;)+$/u.test(rendered[i + 1])
      if (
        token.nesting === 1 &&
        (!precedesSpace || underscore) &&
        letter.test(before) &&
        (underscore || punctuation.test(after))
      ) {
        const previous = tokens[i - 1]
        if (previous?.type === 'text') {
          previous.content = previous.content.replace(
            /[^\s]$/u,
            (char) => `&#${char.codePointAt(0)};`,
          )
          changed ||= rendered[i - 1] !== previous.content
          rendered[i - 1] = previous.content
        }
      }
      if (
        token.nesting === -1 &&
        (!followsSpace || underscore) &&
        letter.test(after) &&
        (underscore || punctuation.test(before))
      ) {
        const next = tokens[i + 1]
        if (next?.type === 'text') {
          next.content = next.content.replace(
            /^[^\s]/u,
            (char) => `&#${char.codePointAt(0)};`,
          )
          changed ||= rendered[i + 1] !== next.content
          rendered[i + 1] = next.content
        }
      }
    }
  }
  return tokens
}

function escapeInlineEdgeSpaces(tokens: Token[]): Token[] {
  const firstTextIndex = findEdgeTextToken(tokens, false)
  const lastTextIndex = findEdgeTextToken(tokens, true)
  return escapeInlineBoundarySpaces(tokens, firstTextIndex, lastTextIndex)
}

function escapeInlineBoundarySpaces(
  tokens: Token[],
  firstTextIndex: number,
  lastTextIndex: number,
): Token[] {
  return tokens.flatMap((token, index) => {
    if (token.type !== 'text') return [token]

    // A line break strips the spaces around it.
    const lineBreak = (neighbor: Token | undefined) => neighbor?.type === 'hardbreak' || neighbor?.type === 'softbreak'
    const escapeLeading =
      index === firstTextIndex || isFormatDelimiter(tokens[index - 1], 1) || lineBreak(tokens[index - 1])
    const escapeTrailing =
      index === lastTextIndex || isFormatDelimiter(tokens[index + 1], -1) || lineBreak(tokens[index + 1])
    // Spaces and line breaks there are trimmed or block flanking, so they are written as entities.
    const leading = escapeLeading ? (/^[ \n]+/u.exec(token.content)?.[0] ?? '') : ''
    const trailing = escapeTrailing ? (/[ \n]+$/u.exec(token.content)?.[0] ?? '') : ''

    if (!leading && !trailing) return [token]
    if (leading.length + trailing.length >= token.content.length) {
      return [whitespaceEntityToken(token.content)]
    }

    const result: Token[] = []
    if (leading) result.push(whitespaceEntityToken(leading))
    token.content = token.content.slice(
      leading.length,
      trailing ? -trailing.length : undefined,
    )
    result.push(token)
    if (trailing) result.push(whitespaceEntityToken(trailing))
    return result
  })
}

/**
 * Inline Markdown for a one-line slot (directive brackets, an attribute value): the slot ends at the line and trims its
 * edges, so a line break and an edge space are written as entities.
 */
function oneLineInline(tokens: Token[]): string {
  const md = defaultMarkdown.renderer.renderInline(oneLineTokens(escapeInlineEdgeSpaces(tokens)), {}, {})
  // A directive's `[...]` ends at the bracket that balances it, escaped or not (stored documents are read that way), so
  // escaped text brackets are written as entities when they are unbalanced.
  let depth = 0
  let balanced = true
  for (const char of md) {
    if (char === '[') depth++
    else if (char === ']' && --depth < 0) balanced = false
  }
  if (balanced && depth === 0) return md
  return md.replaceAll(/\\(\\|[[\]])/gu, (match: string, char: string) =>
    char === '[' ? '&#91;' : char === ']' ? '&#93;' : match)
}

function oneLineTokens(tokens: Token[]): Token[] {
  return tokens.map((token) => {
    if (token.type === 'text') token.content = token.content.replaceAll('\n', '&#10;')
    if (token.type !== 'softbreak') return token
    const entity = new Token('html_inline', '', 0)
    entity.content = '&#10;'
    return entity
  })
}

/**
 * An attribute value holding inline Markdown, on one line with its edge spaces kept. The attribute writer quotes it
 * (`key:="json"` when it holds a quote or a brace).
 */
function attributeInline(blocks: Blocks, options: BlocksToMdOptions): string {
  return defaultMarkdown.renderer.renderInline(oneLineTokens(escapeInlineEdgeSpaces(inlineBlocksToTokens(blocks, true, options))), {}, {})
}

/** A caption or label attribute: one paragraph or an inline run is written on one line; other blocks keep their Markdown. */
function attributeMarkdown(blocks: Blocks, options: BlocksToMdOptions): string {
  const [only] = blocks
  if (blocks.length === 1 && only.type === 'p' && !only.align) return attributeInline(only.children, options)
  if (blocks.every((block) => INLINE_BLOCK_TYPES.has(block.type))) return attributeInline(blocks, options)
  return blocksToMd(blocks, options).trim()
}

function isFormatDelimiter(token: Token | undefined, nesting: 1 | -1): boolean {
  return (
    token?.nesting === nesting &&
    (token.type === 'strong_open' ||
      token.type === 'strong_close' ||
      token.type === 'em_open' ||
      token.type === 'em_close' ||
      token.type === 's_open' ||
      token.type === 's_close')
  )
}

function findEdgeTextToken(tokens: Token[], reverse: boolean): number {
  const indexes = reverse
    ? Array.from(tokens.keys()).toReversed()
    : Array.from(tokens.keys())
  for (const index of indexes) {
    const token = tokens[index]
    if (token.type === 'text') return index
    if (token.nesting === 0) return -1
  }
  return -1
}

function whitespaceEntityToken(whitespace: string): Token {
  const token = new Token('html_inline', '', 0)
  token.content = whitespace.replaceAll(' ', '&#32;').replaceAll('\n', '&#10;')
  return token
}

function transformedToInlineTokens(
  transformed: AppliedTransform,
  shouldEscape: boolean,
  options: BlocksToMdOptions,
): Token[] | undefined {
  switch (transformed.type) {
    case 'default':
      return undefined
    case 'omit':
      return []
    case 'string':
      return stringToInlineTokens(transformed.value, shouldEscape)
    case 'block':
      return inlineBlockToTokens(transformed.value, shouldEscape, options)
    case 'blocks':
      return inlineBlocksToTokens(transformed.value, shouldEscape, options)
  }
  return undefined
}

function stringToInlineTokens(text: string, shouldEscape: boolean): Token[] {
  if (text.length === 0) return []
  const parts = text.split('\n')
  const tokens: Token[] = []

  for (let i = 0; i < parts.length; i++) {
    if (i > 0) tokens.push(new Token('softbreak', 'br', 0))
    if (!parts[i]) continue

    const token = new Token('text', '', 0)
    token.content = shouldEscape ? escapeMarkdown(parts[i]) : parts[i]
    tokens.push(token)
  }

  return tokens
}

function addPromptMetadataAttributes(
  attributes: Record<string, string>,
  block: {
    promptType?: unknown
    importance?: unknown
    difficulty?: unknown
    practice?: unknown
  },
) {
  if (block.practice === true) attributes.practice = 'true'
  if (typeof block.promptType === 'string' && block.promptType) {
    attributes.type = block.promptType
  }
  if (isPromptLevel(block.importance)) attributes.importance = block.importance
  if (isPromptLevel(block.difficulty)) attributes.difficulty = block.difficulty
}

function addCriteriaAttribute(
  attributes: Record<string, string>,
  block: { subtype?: unknown; criteria?: unknown },
) {
  if (block.subtype !== 'la') return
  if (!Array.isArray(block.criteria) || block.criteria.length === 0) return
  attributes.criteria = JSON.stringify(block.criteria).replaceAll('"', "'")
}

function addSpokenResponseAnswerAttributes(
  attributes: Record<string, string>,
  block: { subtype?: unknown; answer?: unknown },
) {
  if (block.subtype !== 'sr') return
  if (
    typeof block.answer !== 'object' ||
    block.answer === null ||
    !('src' in block.answer) ||
    typeof block.answer.src !== 'string' ||
    !('transcript' in block.answer) ||
    typeof block.answer.transcript !== 'string'
  ) {
    return
  }
  attributes.answerSrc = block.answer.src
  attributes.answerTranscript = block.answer.transcript
}

function isInlineQuestionPrompt(blocks: Blocks): boolean {
  return blocks.every(
    (block) =>
      (block.type === 'review' && block.display === 'inline') ||
      [
        'text',
        'chars',
        'br',
        'code',
        'a',
        'em',
        'l',
        'mention',
        'cloze',
        'comment',
        'criticComment',
        'diff',
        'page',
      ].includes(block.type),
  )
}

function isPromptLevel(value: unknown): value is 'low' | 'medium' | 'high' {
  return value === 'low' || value === 'medium' || value === 'high'
}

function textEscape(options: BlocksToMdOptions): (text: string) => string {
  return options.legacyTextEscape ? legacyEscapeMarkdown : escapeMarkdown
}

/** The escapes the writer used before context-aware escaping, kept for the parser's legacy text comparisons. */
function legacyEscapeMarkdown(text: string): string {
  return text.replaceAll('\\', '\\\\').replaceAll('&', '&amp;').replaceAll('*', '\\*').replaceAll('~', '\\~')
    .replaceAll('$', '\\$')
}

/**
 * Literal text as Markdown the parser reads back as the same text. A character is escaped only where it could start or
 * close syntax. A run edge counts as unknown (formatting, an atom or a line start may be next to it), so a
 * character's rule treats a missing neighbor as the dangerous one.
 */
function escapeMarkdown(text: string): string {
  const escaped = new Set<number>()
  const at = (index: number): string | undefined => text[index]
  const word = (char: string | undefined) => char !== undefined && /[\p{L}\p{N}]/u.test(char)
  const lineRest = (index: number) => text.slice(index, (text.indexOf('\n', index) + 1 || text.length + 1) - 1)
  // Brackets: a text bracket without its partner in the run always (a directive's `[...]` counts brackets); a pair
  // whose `]` could open a link, reference, attributes or definition, or close a wiki link, both.
  const open: number[] = []
  const partner = new Map<number, number>()
  for (let index = 0; index < text.length; index++) {
    if (text[index] === '[') open.push(index)
    else if (text[index] === ']') {
      const start = open.pop()
      if (start === undefined) escaped.add(index)
      else partner.set(index, start)
    }
  }
  for (const index of open) escaped.add(index)
  for (const [close, start] of partner) {
    const next = at(close + 1)
    // `[[` opens a wiki link, whatever follows.
    if (next === undefined || '([{:]'.includes(next) || text[start + 1] === '[') escaped.add(close).add(start)
  }
  for (let index = 0; index < text.length; index++) {
    const char = text[index]
    const previous = at(index - 1)
    const next = at(index + 1)
    const lineStart = previous === undefined || previous === '\n'
    switch (char) {
      // Always syntax: escapes, emphasis and strikethrough delimiters, math, code spans.
      case '\\': case '*': case '~': case '$': case '`':
        escaped.add(index)
        break
      // Emphasis unless inside a word (`snake_case`).
      case '_':
        if (!word(previous) || !word(next)) escaped.add(index)
        break
      // Highlight `==`; a line of `=` under text is a heading underline.
      case '=':
        if (previous === undefined || next === undefined || previous === '=' || next === '=' || previous === '\n' ||
          next === '\n') escaped.add(index)
        break
      // Inline HTML, autolinks (an email's local part may start with most punctuation) and `<--COMMENTS`.
      case '<':
        if (next === undefined || !/\s/.test(next)) escaped.add(index)
        break
      // A mention after a space.
      case '@':
        if ((previous === undefined || /\s/.test(previous)) && (next === undefined || /[\w-]/.test(next))) escaped.add(index)
        break
      // A directive `::name` (inline) or `:::name` (block).
      case ':':
        if (next === ':' && (at(index + 2) === undefined || /[\w-]/.test(at(index + 2) ?? ''))) escaped.add(index)
        else if (lineStart && /^:?-[-|: \t]*$/.test(lineRest(index))) escaped.add(index)
        break
      // CriticMarkup `{++ {-- {>> {== {~~`, or attributes after the element before this text.
      case '{':
        if (previous === undefined || /^(\+\+|--|>>|==|~~)/.test(text.slice(index + 1, index + 3))) escaped.add(index)
        break
      // A CriticMarkup close (`++}`, `--}`, `==}`, `~~}`, `<<}`) inside a suggestion or comment.
      case '}':
        if (/(?:\+\+|--|==|~~|<<)$/.test(text.slice(0, index))) escaped.add(index)
        break
      // Line starts: headings (and a heading's closing `#`s), quotes, list items, rules, heading underlines, table
      // delimiter rows.
      case '#':
        if ((lineStart && /^#{1,6}(?:[ \t]|$)/.test(lineRest(index))) ||
          (previous === ' ' && /^#+[ \t]*$/.test(lineRest(index)))) escaped.add(index)
        break
      case '>':
        if (lineStart) escaped.add(index)
        break
      case '-': case '+':
        if (lineStart && (next === undefined || /[ \t\n]/.test(next) || /^[-+|: \t]*$/.test(lineRest(index)))) escaped.add(index)
        break
      case '|':
        if (lineStart && /^\|[-|: \t]*$/.test(lineRest(index)) && lineRest(index).includes('-')) escaped.add(index)
        break
      default:
        if (lineStart && /\d/.test(char)) {
          const marker = /^\d{1,9}([.)])(?:[ \t]|$)/.exec(lineRest(index))
          if (marker) escaped.add(index + marker[0].indexOf(marker[1]))
        }
    }
  }
  let result = ''
  for (let index = 0; index < text.length; index++) {
    const char = text[index]
    // Spaces beside a line break would become a hard break or be stripped as indentation.
    if (char === ' ' && (/^ *\n/.test(text.slice(index)) || /\n *$/.test(text.slice(0, index)))) result += '&#32;'
    // A line break ending a blank line would end the paragraph.
    else if (char === '\n' && /\n *$/.test(text.slice(0, index))) result += '&#10;'
    else if (char === '&') result += '&amp;'
    else result += escaped.has(index) ? `\\${char}` : char
  }
  return result
}

function inlineBlockToTokens(
  block: Block,
  shouldEscape: boolean = true,
  options: BlocksToMdOptions = {},
  transformation = applyTransform(block, options),
): Token[] {
  const transformed = transformedToInlineTokens(
    transformation,
    shouldEscape,
    options,
  )
  if (transformed) return transformed

  const tokens: Token[] = []

  switch (block.type) {
    case 'br': {
      tokens.push(new Token('hardbreak', 'br', 0))
      break
    }

    case 'text': {
      const text = block.text
      // Handle newlines as softbreaks
      if (block.escaped) {
        tokens.push(escapedTextToken(text))
      } else if (text === '\n') {
        const token = new Token('softbreak', 'br', 0)
        tokens.push(token)
      } else {
        const token = new Token('text', '', 0)
        token.content = shouldEscape ? textEscape(options)(text) : text
        tokens.push(token)
      }
      break
    }

    case 'chars': {
      if (block.escaped) {
        tokens.push(escapedTextToken(getTextFromChars(block), block.format))
        break
      }
      tokens.push(...escapeFormatFlanking(formatRunsToTokens(
        [{
          text: getTextFromChars(block),
          format: block.format ?? 0,
          escaped: block.escaped,
        }],
        shouldEscape,
        0,
        textEscape(options),
      )))
      break
    }

    case 'l': {
      const token = new Token('directive_inline', 'span', 0)
      token.markup = '::'
      token.meta = {
        name: 'l',
        content: oneLineInline(inlineBlocksToTokens(block.children, !options.legacyTextEscape, options)),
        attributes: { lang: block.lang },
      }
      tokens.push(token)
      break
    }

    case 'em': {
      // Em with color - use directive
      const color = (block as any).color
      if (color && color !== 'default') {
        const token = new Token('directive_inline', 'span', 0)
        token.markup = '::'
        const content = oneLineInline(inlineBlocksToTokens(block.children, !options.legacyTextEscape, options))
        token.meta = {
          name: 'mark',
          content,
          attributes: { color },
        }
        tokens.push(token)
      } else {
        // Regular mark without color
        const openToken = new Token('mark_open', 'mark', 1)
        const children = inlineBlocksToTokens(block.children, !options.legacyTextEscape, options)
        const closeToken = new Token('mark_close', 'mark', -1)
        tokens.push(openToken, ...children, closeToken)
      }
      break
    }

    case 'code': {
      // 'code' type now implies latex:true (plain inline code is format & CODE on chars)
      const content = inlineBlocksToPlainText(block.children, options)
      // Empty $...$ becomes a display-math opener and swallows following blocks.
      if (content) {
        const token = new Token('math_inline', '', 0)
        token.content = content
        token.markup = '$'
        tokens.push(token)
      } else {
        const token = new Token('directive_inline', 'span', 0)
        token.markup = '::'
        token.meta = { name: 'latex', content: '', attributes: {} }
        tokens.push(token)
      }
      break
    }

    case 'a': {
      const openToken = new Token('link_open', 'a', 1)
      openToken.attrSet('href', block.href)
      // A link whose text reads as a page card (`---`, or its own non-web href) says it is a link.
      const text = blocksToMd(block.children, { ...options, legacyTextEscape: true })
      if (text === '---' || (text === block.href && !block.href.startsWith('http'))) openToken.attrSet('type', 'link')
      const children = inlineBlocksToTokens(block.children, true, options)
      const closeToken = new Token('link_close', 'a', -1)

      tokens.push(openToken, ...children, closeToken)
      break
    }

    case 'page': {
      const openToken = new Token('link_open', 'a', 1)
      openToken.attrSet('href', block.pageId)
      const textToken = new Token('text', '', 0)
      textToken.content = block.pageId
      const closeToken = new Token('link_close', 'a', -1)

      tokens.push(openToken, textToken, closeToken)
      break
    }

    case 'pageLink': {
      const target = options.pageLinkTarget?.(block.pageId, block.spaceId) ?? (block.spaceId === undefined ? block.pageId : `space:${encodeURIComponent(block.spaceId)}/${encodeURIComponent(block.pageId)}`)
      const openToken = new Token('wikilink_open', 'a', 1)
      openToken.attrSet('href', target)
      openToken.markup = '[['
      const textToken = new Token('text', '', 0)
      textToken.content = target
      const closeToken = new Token('wikilink_close', 'a', -1)
      closeToken.markup = ']]'

      tokens.push(openToken, textToken, closeToken)
      break
    }

    case 'pages': {
      const openToken = new Token('link_open', 'a', 1)
      openToken.attrSet('href', block.ids.join(','))
      const textToken = new Token('text', '', 0)
      textToken.content = '---'
      const closeToken = new Token('link_close', 'a', -1)

      tokens.push(openToken, textToken, closeToken)
      break
    }

    case 'comment': {
      const token = new Token('directive_inline', 'span', 0)
      token.markup = '::'
      token.meta = {
        name: 'comment',
        content: blocksToMd(block.children, { ...options, trim: false }),
        attributes: { commentId: block.commentId },
      }
      tokens.push(token)
      break
    }

    case 'criticComment': {
      const token = new Token('critic_comment', 'span', 0)
      token.content = block.body
      tokens.push(token)
      break
    }

    case 'diff': {
      const token = new Token('directive_inline', 'span', 0)
      token.meta = { name: 'diff', content: oneLineInline(inlineBlocksToTokens(block.children, shouldEscape, options)), attributes: { kind: block.kind } }
      tokens.push(token)
      break
    }

    case 'review': {
      tokens.push(...reviewToInlineTokens(block, options))
      break
    }

    case 'mention': {
      // Preserve @username syntax when parsed from @-form
      if (block.subtype === 'user' && block.$at) {
        const openToken = new Token('mention_open', 'a', 1)
        openToken.attrSet('href', block.uid)
        openToken.markup = '@'
        const textToken = new Token('text', '', 0)
        textToken.content = `@${block.uid}`
        const closeToken = new Token('mention_close', 'a', -1)
        closeToken.markup = '@'
        tokens.push(openToken, textToken, closeToken)
        break
      }

      const token = new Token('directive_inline', 'span', 0)
      token.markup = '::'
      token.meta = { name: 'mention', content: mentionContent(block), attributes: {} }
      tokens.push(token)
      break
    }

    case 'cloze': {
      const token = new Token('directive_inline', 'span', 0)
      token.markup = '::'
      const content = oneLineInline(inlineBlocksToTokens(block.children, true, options))

      // An empty qid is omitted, as on every other question block; parsing gives it a fresh one.
      const attributes: Record<string, string> = block.qid ? { qid: block.qid } : {}
      if (block.fractionalIndex) {
        attributes.fractionalIndex = block.fractionalIndex
      }
      if (block.tip !== undefined) attributes.tip = block.tip
      if (block.practice) attributes.practice = 'true'
      if (block.answerRegexp) {
        attributes.answerRegexp = block.answerRegexp
      }
      if (block.answerAudio) {
        attributes.answerAudioSrc = block.answerAudio.src
      }
      addPromptMetadataAttributes(attributes, block)

      token.meta = {
        name: 'cloze',
        content,
        attributes,
      }
      tokens.push(token)
      break
    }

    case 'question': {
      // Only handle inline questions here
      if ((block as any).inline) {
        const token = new Token('directive_inline', 'span', 0)
        token.markup = '::'

        const subtype = block.subtype
        const attributes: Record<string, string> = {}
        if (block.qid) attributes.qid = block.qid
        if ((block as any).fractionalIndex)
          attributes.fractionalIndex = (block as any).fractionalIndex
        addPromptMetadataAttributes(attributes, block)

        const answer = (block as any).answer
        if (subtype === 'sa') {
          attributes.answer = JSON.stringify(answer).replaceAll('"', "'")
          if ((block as any).strict) attributes.class = 'strict'
          if ((block as any).answerRegexp)
            attributes.answerRegexp = (block as any).answerRegexp
        } else if (subtype === 'sc' || subtype === 'mc') {
          const opts = block.opts.map((option) => blocksToMd(option, options))
          attributes.opts = JSON.stringify(opts).replaceAll('"', "'")
          attributes.answer =
            subtype === 'sc' ? String(answer) : JSON.stringify(answer)
        }

        if ((block as any).explanation) {
          const explText = blocksToMd(
            (block as any).explanation,
            options,
          ).trim()
          attributes.explanation = explText
        }

        token.meta = {
          name: subtype,
          content: '',
          attributes,
        }
        tokens.push(token)
      }
      break
    }

    case 'img':
    case 'preview': {
      const token = new Token('image', 'img', 0)
      const attrs: [string, string][] = []

      // Get src/alt/title for standard image format
      const src = (block as any).src ?? ''
      const alt = (block as any).alt ?? ''
      const title = (block as any).title

      // Standard attrs for markdown image
      attrs.push(['src', src], ['alt', alt])
      if (title) attrs.push(['title', title])

      // Copy other properties as custom attributes
      for (const [key, value] of Object.entries(block)) {
        if (
          !key.startsWith('$') &&
          key !== 'id' &&
          key !== 'type' &&
          key !== 'src' &&
          key !== 'alt' &&
          key !== 'title'
        ) {
          if (key === 'subtype') {
            // For preview, subtype becomes type attribute
            attrs.push(['type', String(value)])
          } else if ((key === 'caption' || key === 'children') && Array.isArray(value)) {
            // The caption's blocks, the label's inline run (empty when the label is `[]`)
            attrs.push([key, attributeMarkdown(value as Blocks, options)])
            if ((value as Blocks).every(child => INLINE_BLOCK_TYPES.has(child.type))) attrs.push(['captionInline', 'true'])
          } else if (key === 'size' && typeof value === 'number') {
            attrs.push([key, String(value)])
          } else if (key === 'width' && typeof value === 'number') {
            attrs.push([key, percentWidth(value)])
          } else {
            attrs.push([key, String(value)])
          }
        }
      }

      token.attrs = attrs
      token.children = []
      token.content = alt
      tokens.push(token)
      break
    }

    case 'video': {
      const openToken = new Token('link_open', 'a', 1)
      openToken.attrSet('href', block.src)
      openToken.attrSet('type', 'video')
      if (block.width) openToken.attrSet('width', String(block.width))
      if (block.autoplay) openToken.attrSet('autoplay', '')

      // The reader re-reads the link text as the caption's Markdown after re-escaping it the way the writer used to, so the
      // link text holds a caption whose Markdown that earlier spelling writes the same; any other is the attribute.
      const textToken = new Token('text', '', 0)
      const caption = block.caption ?? []
      const md = caption.length > 0 ? blocksToMd(caption, options).trim() : ''
      // A link label ends at a bracket even inside a code span, and cannot hold a line break.
      const linkText = !/[[\]\n]/.test(md) && md === blocksToMd(caption, { ...options, legacyTextEscape: true }).trim()
      if (caption.length > 0 && caption.every(child => INLINE_BLOCK_TYPES.has(child.type))) openToken.attrSet('captionInline', 'true')
      if (linkText) textToken.content = md
      else {
        openToken.attrSet('caption', attributeMarkdown(caption, options))
      }

      const closeToken = new Token('link_close', 'a', -1)
      tokens.push(openToken, textToken, closeToken)
      break
    }

    case 'audio': {
      const openToken = new Token('link_open', 'a', 1)
      openToken.attrSet('href', block.src)
      openToken.attrSet('type', 'audio')
      if (block.autoplay) openToken.attrSet('autoplay', '')

      const textToken = new Token('text', '', 0)
      textToken.content = ''

      const closeToken = new Token('link_close', 'a', -1)
      tokens.push(openToken, textToken, closeToken)
      break
    }

    case 'file': {
      const openToken = new Token('link_open', 'a', 1)
      openToken.attrSet('href', block.src)
      openToken.attrSet('type', 'file')
      openToken.attrSet('size', String(block.size))

      const textToken = new Token('text', '', 0)
      textToken.content = block.name

      const closeToken = new Token('link_close', 'a', -1)
      tokens.push(openToken, textToken, closeToken)
      break
    }

    // Block-only types produce no inline tokens.
    case 'definition':
    case 'alert':
    case 'col':
    case 'cols':
    case 'comments':
    case 'h':
    case 'hr':
    case 'li':
    case 'list':
    case 'p':
    case 'pageMeta':
    case 'pageTitle':
    case 'pre':
    case 'quote':
    case 'root':
    case 'rule':
    case 'questionGroup':
    case 'table':
    case 'td':
    case 'tr':
      break
  }

  return tokens
}

function reviewToInlineTokens(
  block: Extract<Block, { type: 'review' }>,
  options: BlocksToMdOptions,
): Token[] {
  const canonical = (block.change === 'update' && block.children.length === 2 && block.children[0]?.type === 'diff' && block.children[0].kind === 'delete' && block.children[1]?.type === 'diff' && block.children[1].kind === 'add') || ((block.change === 'add' || block.change === 'delete') && block.children.length === 1 && block.children[0]?.type === 'diff' && block.children[0].kind === block.change)
  if (!canonical || block.display === 'block') {
    const token = new Token('directive_inline', 'span', 0)
    token.meta = { name: 'review', content: oneLineInline(inlineBlocksToTokens(block.children, true, options)), attributes: { change: block.change, state: block.state, ...(block.display === 'block' ? { display: block.display } : {}) } }
    return [token]
  }
  let tokens: Token[]
  const deleted = block.children[0]
  const added = block.children[1]
  if (
    block.change === 'update' &&
    block.children.length === 2 &&
    deleted?.type === 'diff' &&
    deleted.kind === 'delete' &&
    added?.type === 'diff' &&
    added.kind === 'add'
  ) {
    const oldContent = renderInlineMarkdown(deleted.children, options)
    const newContent = renderInlineMarkdown(added.children, options)
    const token = new Token('diff_inline', 'span', 0)
    token.content = `${oldContent}~>${newContent}`
    token.markup = '~~'
    token.meta = { kind: 'update', old: oldContent, new: newContent }
    tokens = [token]
  } else if (block.change === 'add' || block.change === 'delete') {
    const matchingDiff =
      block.children.length === 1 &&
      deleted?.type === 'diff' &&
      deleted.kind === block.change
        ? deleted
        : undefined
    const content = renderInlineMarkdown(
      matchingDiff?.children ?? block.children,
      options,
    )
    const token = new Token('diff_inline', 'span', 0)
    token.content = content
    token.markup = block.change === 'add' ? '++' : '--'
    token.meta = { kind: block.change }
    tokens = [token]
  } else {
    tokens = inlineBlocksToTokens(block.children, true, options)
  }
  if (block.state === 'approved') {
    const metadata = new Token('critic_comment', 'span', 0)
    metadata.content = 'review:approved'
    metadata.markup = '>>'
    tokens.push(metadata)
  }
  return tokens
}

// CriticMarkup content trims its edges like a paragraph.
function renderInlineMarkdown(blocks: Blocks, options: BlocksToMdOptions) {
  return defaultMarkdown.renderer.renderInline(
    escapeInlineEdgeSpaces(inlineBlocksToTokens(blocks, true, options)),
    {},
    {},
  )
}

/** A width (block.ts: a percentage, parser.ts readPercentWidth) as the markdown attribute `50%`. */
function percentWidth(width: number | string): string {
  return typeof width === 'number' ? `${width}%` : width
}

/** Front matter keys in `pageMeta`'s declared order (block.ts), others after them sorted: a CRDT view's key order is
 * its storage order (crdt_rs sorts attrs), which no replica authored, so the markdown must not depend on it. */
const PAGE_META_KEYS = ['id', 'title', 'color', 'image', 'icon', 'tags', 'emoji']
function pageMetaOrder<T extends object>(meta: T): T {
  const rank = (key: string) => { const at = PAGE_META_KEYS.indexOf(key); return at < 0 ? PAGE_META_KEYS.length : at }
  return Object.fromEntries(Object.entries(meta).toSorted(([a], [b]) => rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0))) as T
}
